import { useMemo, useState } from "react";
import { Icon } from "../../components/Icon";
import { Badge, Btn, Card, Empty, Meter, PageHead, Seg, Skeleton } from "../../components/kit";
import { useCurriculum, type PathLesson, type PathTrack } from "../../lib/hooks";

/* The unified Curriculum page — "My path" (the tracks + lessons the learner is
   working through, assigned or self-added) and "Catalog" (the approved tracks +
   lessons they can add). Clicking a lesson launches it on the Orbit desktop app
   via the adaptr://lesson?id=… deep link, at whatever fade level the engine has
   the learner on for that skill. */

const FADE_LABEL: Record<string, string> = {
  guided: "Guided", prompted: "Prompted", independent: "Independent", teach: "Teach-back",
};

type StatusMeta = { label: string; ic: string; cls: string; cta: string; ctaIcon: string; ctaVariant: "live" | "ghost" };
const STATUS_META: Record<PathLesson["status"], StatusMeta> = {
  retired: { label: "Complete", ic: "check-check", cls: "mastered", cta: "Review", ctaIcon: "rotate-ccw", ctaVariant: "ghost" },
  in_progress: { label: "In progress", ic: "loader", cls: "active", cta: "Resume", ctaIcon: "play", ctaVariant: "live" },
  available: { label: "Ready", ic: "play", cls: "ready", cta: "Start", ctaIcon: "play", ctaVariant: "live" },
  locked: { label: "Locked", ic: "lock", cls: "locked", cta: "", ctaIcon: "", ctaVariant: "ghost" },
};

/** Launch a lesson on the Orbit desktop app. The desktop deep-link handler sets
   lesson mode and runs the specific lesson at the learner's current fade level. */
function launchLesson(lessonId: string) {
  window.location.href = `adaptr://lesson?id=${encodeURIComponent(lessonId)}`;
}

function LessonRow({ lesson }: { lesson: PathLesson }) {
  const m = STATUS_META[lesson.status];
  const fade = lesson.fadeLevel ? FADE_LABEL[lesson.fadeLevel] ?? lesson.fadeLevel : null;
  const blockers = lesson.status === "locked" ? (lesson.blockedBy ?? []) : [];
  return (
    <div className={`lsn ${m.cls}`}>
      <span className={`lsn-state ${m.cls}`}><Icon name={m.ic} size={18} strokeWidth={2} /></span>
      <div className="lsn-body">
        <div className="lsn-top">
          <span className="lsn-t">{lesson.title}</span>
          {fade && lesson.status !== "locked" && <span className={`level-tag ${(lesson.fadeLevel ?? "").toLowerCase()}`}>{fade}</span>}
        </div>
        <div className="lsn-meta">
          <span><Icon name={m.ic} size={12} />{m.label}</span>
          {lesson.durationMinutes ? (<><span className="dotsep">·</span><span><Icon name="clock" size={12} />{lesson.durationMinutes} min</span></>) : null}
        </div>
        {blockers.length > 0 && (
          <div className="lsn-gate">
            <span className="lsn-gate-label"><Icon name="lock" size={12} />{blockers.length > 1 ? "Complete these first" : "Complete this first"}</span>
            <div className="lsn-gate-list">
              {blockers.map((b) => (
                <button key={b.lessonId} className="lsn-gate-chip" title={`Start “${b.title}”`} onClick={() => launchLesson(b.lessonId)}>
                  <Icon name="play" size={12} strokeWidth={2.4} />{b.title}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {lesson.status === "locked" ? (
        <span className="lsn-lock"><Icon name="lock" size={16} /></span>
      ) : (
        <div className="lsn-act">
          <Btn variant={m.ctaVariant} size="sm" icon={m.ctaIcon} onClick={() => launchLesson(lesson.lessonId)}>{m.cta}</Btn>
        </div>
      )}
    </div>
  );
}

function PathTrackCard({ track, onRemove }: { track: PathTrack; onRemove: () => void }) {
  const pct = track.total > 0 ? (track.mastered / track.total) * 100 : 0;
  return (
    <Card
      title={track.name}
      icon={track.icon || "route"}
      action={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Badge tone={track.source === "assigned" ? "live" : "violet"}>{track.source === "assigned" ? "Assigned" : "Added"}</Badge>
          <div className="trk-prog"><Meter pct={pct} /><span className="muted-sm">{track.mastered}/{track.total}</span></div>
          {track.source !== "assigned" && (
            <button className="icon-mini" title="Remove from path" onClick={onRemove}><Icon name="x" size={14} /></button>
          )}
        </div>
      }
      pad={false}
    >
      <div className="lesson-list">
        {track.lessons.length === 0
          ? <div style={{ padding: 14 }}><span className="muted-sm">No lessons in this track yet.</span></div>
          : track.lessons.map((l) => <LessonRow key={l.lessonId} lesson={l} />)}
      </div>
    </Card>
  );
}

export function CurriculumPage() {
  const { path, catalog, addItem, removeItem } = useCurriculum();
  const [tab, setTab] = useState<"My path" | "Catalog">("My path");
  const [query, setQuery] = useState("");
  const loadingPath = !path;

  const summary = useMemo(() => {
    if (!path) return { tracks: 0, lessons: 0, mastered: 0 };
    const trackLessons = path.tracks.reduce((n, t) => n + t.total, 0);
    const trackMastered = path.tracks.reduce((n, t) => n + t.mastered, 0);
    const soloMastered = path.lessons.filter((l) => l.status === "retired").length;
    return {
      tracks: path.tracks.length,
      lessons: trackLessons + path.lessons.length,
      mastered: trackMastered + soloMastered,
    };
  }, [path]);

  const q = query.trim().toLowerCase();
  const catTracks = (catalog?.tracks ?? []).filter((t) => !q || t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
  const catLessons = (catalog?.lessons ?? []).filter((l) => !q || l.title.toLowerCase().includes(q) || l.objective.toLowerCase().includes(q));

  const pathEmpty = !loadingPath && path.tracks.length === 0 && path.lessons.length === 0;

  return (
    <div className="page">
      <PageHead
        title="Curriculum"
        sub="Your learning path and the full catalog in one place. Pick up a lesson to launch it on the Orbit desktop app."
        actions={
          tab === "Catalog"
            ? <div className="search-inline"><Icon name="search" size={15} /><input placeholder="Search catalog…" value={query} onChange={(e) => setQuery(e.target.value)} /></div>
            : undefined
        }
      />

      <Seg options={["My path", "Catalog"]} value={tab} onChange={(v) => setTab(v as "My path" | "Catalog")} />

      {tab === "My path" ? (
        <>
          <div className="lp-band" style={{ marginTop: 16 }}>
            <div className="lp-prog">
              <div className="pb-eye">Your path</div>
              {loadingPath ? (
                <Skeleton w={220} h={26} style={{ margin: "6px 0 12px" }} />
              ) : (
                <div className="lp-big">{summary.mastered}<span>/ {summary.lessons} lessons complete</span></div>
              )}
              <div className="lp-bar"><Meter pct={summary.lessons > 0 ? (summary.mastered / summary.lessons) * 100 : 0} height={9} /></div>
              <div className="brk">
                <span className="brk-chip"><span className="bd mastered" />{summary.tracks} tracks</span>
                <span className="brk-chip"><span className="bd ready" />{summary.lessons} lessons</span>
              </div>
            </div>
          </div>

          <div className="stack-lg" style={{ marginTop: 18 }}>
            {loadingPath && Array.from({ length: 2 }).map((_, i) => (
              <Card key={`s-${i}`} title="" icon="loader" pad={false}>
                <div className="lesson-list">
                  {Array.from({ length: 3 }).map((_, j) => (
                    <div className="lsn" key={j} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <Skeleton w={36} h={36} r={10} />
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}><Skeleton w="55%" h={14} /><Skeleton w="80%" h={11} /></div>
                      <Skeleton w={72} h={30} r={8} />
                    </div>
                  ))}
                </div>
              </Card>
            ))}

            {pathEmpty && (
              <Card title="Your path is empty" icon="route" iconColor="var(--live-dim)">
                <Empty icon="route">Browse the catalog and add a track or lesson to start your path.</Empty>
                <div style={{ marginTop: 12, textAlign: "center" }}>
                  <Btn variant="live" icon="compass" onClick={() => setTab("Catalog")}>Browse catalog</Btn>
                </div>
              </Card>
            )}

            {!loadingPath && path.tracks.map((track) => (
              <PathTrackCard key={track.id} track={track} onRemove={() => removeItem("track", track.id)} />
            ))}

            {!loadingPath && path.lessons.length > 0 && (
              <Card title="Individual lessons" icon="book-open" iconColor="var(--live-dim)" count={path.lessons.length} pad={false}>
                <div className="lesson-list">
                  {path.lessons.map((l) => (
                    <div className="lsn-row-wrap" key={l.lessonId} style={{ display: "flex", alignItems: "stretch" }}>
                      <div style={{ flex: 1, minWidth: 0 }}><LessonRow lesson={l} /></div>
                      {l.source === "assigned"
                        ? <span className="you-tag" style={{ alignSelf: "center", marginRight: 12 }}>Assigned</span>
                        : <button className="icon-mini" title="Remove from path" style={{ alignSelf: "center", marginRight: 12 }} onClick={() => removeItem("lesson", l.lessonId)}><Icon name="x" size={14} /></button>}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </>
      ) : (
        <div className="stack-lg" style={{ marginTop: 18 }}>
          <Card title="Tracks" icon="route" iconColor="var(--violet-500)" count={catalog ? `${catTracks.length} tracks` : undefined} pad={false}>
            {!catalog ? (
              <div className="cat-grid">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={92} r={12} />)}</div>
            ) : catTracks.length === 0 ? (
              <Empty icon="route">No tracks match “{query}”.</Empty>
            ) : (
              <div className="cat-grid">
                {catTracks.map((t) => (
                  <div className="cat-card" key={t.id}>
                    <span className="cat-ic"><Icon name={t.icon || "route"} size={19} /></span>
                    <div className="cat-body">
                      <div className="cat-t">{t.name}</div>
                      <div className="cat-d">{t.description}</div>
                      <div className="cat-n">{t.lessonCount} lessons{t.role && t.role !== "All" ? ` · ${t.role}` : ""}</div>
                    </div>
                    {t.inPath
                      ? <Btn variant="ghost" size="sm" icon="check" disabled>In path</Btn>
                      : <Btn variant="live" size="sm" icon="plus" onClick={() => addItem("track", t.id)}>Add</Btn>}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Lessons" icon="layout-grid" iconColor="var(--live-dim)" count={catalog ? `${catLessons.length} lessons` : undefined} pad={false}>
            {!catalog ? (
              <div className="cat-grid">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} h={92} r={12} />)}</div>
            ) : catLessons.length === 0 ? (
              <Empty icon="layout-grid">No lessons match “{query}”.</Empty>
            ) : (
              <div className="cat-grid">
                {catLessons.map((l) => (
                  <div className="cat-card" key={l.id}>
                    <span className="cat-ic"><Icon name={l.type === "tools" ? "wrench" : l.type === "mindset" ? "compass" : "brain"} size={19} /></span>
                    <div className="cat-body">
                      <div className="cat-t">{l.title}</div>
                      <div className="cat-d">{l.objective}</div>
                      <div className="cat-n">{l.durationMinutes ? `${l.durationMinutes} min` : "Self-paced"}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {l.inPath
                        ? <Btn variant="ghost" size="sm" icon="check" disabled>In path</Btn>
                        : <Btn variant="ghost" size="sm" icon="plus" onClick={() => addItem("lesson", l.id)}>Add</Btn>}
                      <Btn variant="live" size="sm" icon="play" onClick={() => launchLesson(l.id)}>Start</Btn>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
